import React, { useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCamera,
  faImage,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";

import useFetch from "../../../hooks/useFetch";

const ImageUploader = ({
  label,
  value = "",
  rounded = false,
  uploadType = "general",
  uploadUrl,
  token,
  extraHeaders = {},
  onChange,
  onClose,
}) => {
    const authState = useSelector((state) => state.authReducer);
  const userCode = authState?.user?.think5whyscode;
  const inputRef = useRef(null);
  const [fetchData] = useFetch();
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(value);
  const [message, setMessage] = useState("");

  const chooseFile = () => {
    inputRef.current.click();
  };

  const uploadImage = (e) => {
    const file = e.target.files[0];

    if (!file) return;
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
    setMessage("");
  };

  const uploadNow = async () => {
    if (!selectedFile) return;
    const allowedTypes = ["image/jpeg"];
    const maxSize = 100 * 1024; // 100 KB -- maxWidth 256, maxHeight 256

    if (!allowedTypes.includes(selectedFile.type)) {
        setMessage("❌ Only JPG images are allowed.");
        return;
    }

    if (selectedFile.size > maxSize) {
        setMessage("❌ Profile photo size should be less than 100 KB.");
        return;
    }

    const body = new FormData();
    body.append("file", selectedFile);
    body.append("userCode", userCode);

    try {
      setUploading(true);

      const config = {
        url: `/uploads${uploadUrl}`,
        method: "post",
        data: body, 
        headers: {
          Authorization: token,
          "x-user-code": userCode,
          ...extraHeaders,
        },
      };

      const response = await fetchData(config);
      if (response?.file?.filename) {
        const freshUrl = `${response.url || response.file.url}?t=${Date.now()}`;
        onChange(response.file.filename, freshUrl);
        setMessage("✅ Profile photo uploaded successfully.");
        setTimeout(() => setMessage(""), 3000);
        setSelectedFile(null);
        inputRef.current.value = "";
        setPreview(freshUrl);
      } else {
        setMessage("❌ Upload failed. Please try again.");
      }
    } catch {
      setMessage("❌ Unable to upload photo.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex-1 flex-col items-start justify-start">
      <label className="text-xs font-semibold">{label}</label>

      <div
        onClick={chooseFile}
        className={`mt-2 cursor-pointer border overflow-hidden bg-gray-100 flex item-start justify-start ${
          rounded ? "w-36 h-36 rounded-full mx-auto" : "h-44 rounded-xl"
        }`}
      >
        {uploading ? (
          <FontAwesomeIcon icon={faSpinner} spin className="text-4xl" />
        ) : preview || value ? (
          <img
            src={preview || value}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="text-center">
            <FontAwesomeIcon
              icon={faImage}
              className="text-5xl text-gray-400"
            />

            <div className="text-xs mt-2">Click to Upload</div>
          </div>
        )}
      </div>
      <div className="flex flex-row gap-2 mt-3">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-700 text-white w-fit px-3 py-2 rounded-lg text-sm"
          >
            ✕ Close
          </button>
        )}

        <button
          type="button"
          onClick={chooseFile}
          className="bg-pink-700 text-white w-fit px-3 py-2 rounded-lg text-sm flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faCamera} />
          Apply Change
        </button>

        {selectedFile && (
          <button
            type="button"
            disabled={uploading}
            onClick={uploadNow}
            className="bg-green-600 hover:bg-green-700 text-white w-fit px-3 py-2 rounded-lg text-sm flex items-center gap-2"
          >
            {uploading ? "Uploading..." : "⬆ Upload Photo"}
          </button>
        )}
      </div>
      {message && (
        <div
          className={`mt-3 text-sm font-medium ${
            message.startsWith("✅") ? "text-green-600" : "text-red-500"
          }`}
        >
          {message}
        </div>
      )}
      <input
        ref={inputRef}
        hidden
        type="file"
        accept="image/*"
        onChange={uploadImage}
      />
    </div>
  );
};

export default ImageUploader;
